if (!customElements.get('paint-kit-builder')) {
  customElements.define('paint-kit-builder', class PaintKitBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.state = { family: '', finish: '', query: '', product: null, recipe: null, project: {} };
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.products = [...this.querySelectorAll('[data-kit-product]')];
      this.results = this.querySelector('[data-color-results]');
      this.status = this.querySelector('[data-result-status]');
      this.search = this.querySelector('[data-color-search]');
      this.clearButton = this.querySelector('[data-clear-filters]');
      this.entryError = this.querySelector('[data-entry-error]');
      this.systemCatalog = this.readJson(this.querySelector('[data-system-catalog]')) || {};
      this.addKitButton = this.querySelector('[data-add-kit]');
      this.cartStatus = this.querySelector('[data-cart-status]');
      this.bindEvents();
      this.applyInitialState();
      if (!this.state.recipe) this.loadRemainingProducts();
    }

    bindEvents() {
      this.querySelectorAll('[data-filter]').forEach((button) => button.addEventListener('click', () => {
        const key = button.dataset.filter;
        const value = button.dataset.value;
        this.state[key] = this.state[key] === value ? '' : value;
        this.querySelectorAll(`[data-filter="${key}"]`).forEach((choice) => choice.setAttribute('aria-pressed', String(choice === button && this.state[key] === value)));
        this.filterProducts();
      }));

      this.search?.addEventListener('input', () => {
        this.state.query = this.search.value.trim().toLowerCase();
        this.filterProducts();
      });
      this.clearButton?.addEventListener('click', () => this.clearFilters());
      this.querySelectorAll('[data-select-product]').forEach((button) => button.addEventListener('click', () => this.selectProduct(button.closest('[data-kit-product]'))));
      this.querySelectorAll('[data-back]').forEach((button) => button.addEventListener('click', () => this.showStep(button.dataset.back)));
      const projectForm = this.querySelector('[data-project-form]');
      projectForm?.addEventListener('submit', (event) => this.submitProject(event));
      projectForm?.addEventListener('change', (event) => {
        if (event.target.name === 'condition') this.toggleEpoxyColor();
      });
      this.addKitButton?.addEventListener('click', () => this.addCompleteKit());
    }

    applyInitialState() {
      const params = new URLSearchParams(window.location.search);
      const color = params.get('color');
      const recipe = params.get('recipe')?.trim().toLowerCase() || '';
      this.initialProductHandle = params.get('product')?.trim().toLowerCase() || '';
      this.initialColorName = color?.trim() || '';
      const featuredRecipe = recipe || {
        'void-black': 'void-black-nova',
        'void-black-paint-basecoat': 'void-black-nova',
        'phantom-blue-paint-basecoat': 'phantom-blue-mustang',
        'grizzly-gray-paint-basecoat': 'grizzly-gray-camaro',
      }[this.initialProductHandle] || '';
      const projectRecipes = {
        'void-black-nova': { title: 'Void Black Nova', color: 'Void Black', vehicle: '1969 Chevrolet Nova', finish: 'Basecoat / clearcoat', system: 'Void Black plus the complete Linear prep-to-clear system', sealerColor: 'Black', productHandle: 'void-black-paint-basecoat', productUrl: '/products/void-black-paint-basecoat' },
        'green-with-envy-demon': { title: 'Green With Envy Demon', color: 'Chrysler USA Green With Envy P. (PGE)', vehicle: '1972 Dodge Demon', finish: 'OEMatch basecoat / clearcoat', system: 'OEMatch PGE plus the complete Linear prep-to-clear system', productUrl: '/pages/oematch-paint-all-oem-color-codes?oematch=Green%20With%20Envy%20PGE', formulaId: '4936c25e79d5f40a6a4f25521bb7c530dc5c741b', oematchRecordId: '117655', oematchColorId: '140086' },
        'leaf-green-electra': { title: 'Leaf Green Electra', color: 'Buick Leaf Green M. (46U-225L)', vehicle: '1974 Buick Electra', finish: 'Basecoat / clearcoat', system: 'OEMatch Leaf Green plus the complete Linear prep-to-clear system', productUrl: '/pages/oematch-paint-all-oem-color-codes?oematch=Buick%20Leaf%20Green%20M.%2046U-225L', formulaId: 'bf5d4e7c8a1af61855b5227279a6744c3bde8a8a', oematchRecordId: '74199', oematchColorId: '66958', oematchRtsVariantId: '44525346717789', oematchNonReducedVariantId: '44525328990301' },
        'indigo-blue-chevelle': { title: 'Indigo Blue Chevelle', color: 'Indigo Blue', vehicle: '1970 Chevrolet Chevelle', finish: 'Basecoat / clearcoat with faded stripes', system: 'Indigo Blue plus the complete Linear prep-to-clear system', unresolvedGraphics: ['Hypersonic Gray (GA7) faded stripes'] },
        'phantom-blue-mustang': { title: 'Phantom Blue Cobra Mustang', color: 'Phantom Blue', vehicle: '2017 Ford Mustang GT350', finish: 'Custom stripe finish', system: 'Phantom Blue plus the complete Linear prep-to-clear system', sealerColor: 'Black', productHandle: 'phantom-blue-paint-basecoat', productUrl: '/products/phantom-blue-paint-basecoat', unresolvedGraphics: ['Shoreline Beige pearl-white', 'Pearl midcoat', 'Victory Red pinline'] },
        'grizzly-gray-camaro': { title: 'Grizzly Gray Camaro', color: 'Grizzly Gray', vehicle: '1969 Chevrolet Camaro', finish: 'Custom stripe finish', system: 'Grizzly Gray plus the complete Linear prep-to-clear system', sealerColor: 'Gray', productHandle: 'grizzly-gray-paint-basecoat', productUrl: '/products/grizzly-gray-paint-basecoat', unresolvedGraphics: ['Pewter outline', 'White tri-coat stripe', 'Diamond Silver midcoat', 'GM Victory Red pinstripe'] },
        'solar-red-camaro': { title: 'Solar Red Camaro', color: 'Chevrolet Solar Red G6E-338X', vehicle: '1969 Chevrolet Camaro', finish: 'Basecoat / clearcoat with black graphics', system: 'OEMatch Solar Red plus the complete Linear prep-to-clear system', productUrl: '/pages/oematch-paint-all-oem-color-codes?oematch=Chevrolet%20Solar%20Red%20G6E-338X', formulaId: '493dd178fc67e2ab4eb62796655a99f37ba40a64', oematchRecordId: '129027', oematchColorId: '178094', oematchRtsVariantId: '44525353697373', oematchNonReducedVariantId: '44525338591325', unresolvedGraphics: ['Black hood and side graphics'] },
      };
      if (projectRecipes[featuredRecipe]) {
        const projectRecipe = { id: featuredRecipe, ...projectRecipes[featuredRecipe] };
        if (projectRecipe.productHandle) {
          this.initialProductHandle = projectRecipe.productHandle;
          this.initialProjectRecipe = projectRecipe;
          if (this.selectInitialProduct()) return;
        } else {
          this.openProjectRecipe(projectRecipe);
          return;
        }
      }
      if (recipe && !projectRecipes[featuredRecipe]) {
        this.failProjectRecipe();
      } else if (this.initialProductHandle) {
        this.status.textContent = `Loading the kit${this.initialColorName ? ` for ${this.initialColorName}` : ''}…`;
      } else if (color && this.search) {
        this.search.value = color;
        this.state.query = color.trim().toLowerCase();
        this.filterProducts();
      }
      this.selectInitialProduct();
    }

    selectInitialProduct() {
      if (!this.initialProductHandle) return true;
      const product = this.products.find((candidate) => candidate.dataset.productHandle === this.initialProductHandle);
      if (!product) return false;
      this.initialProductHandle = '';
      this.selectProduct(product, this.initialProjectRecipe || null);
      this.initialProjectRecipe = null;
      return true;
    }

    async loadRemainingProducts() {
      const totalPages = Number(this.querySelector('[data-total-pages]')?.dataset.totalPages || 1);
      const collectionUrl = this.dataset.collectionUrl;
      if (!collectionUrl || totalPages < 2) {
        if (!this.selectInitialProduct()) this.failInitialProduct();
        return;
      }
      this.status.textContent = 'Loading the color index…';
      try {
        const requests = Array.from({ length: totalPages - 1 }, (_, index) => {
          const separator = collectionUrl.includes('?') ? '&' : '?';
          return fetch(`${collectionUrl}${separator}view=paint-kit-data&page=${index + 2}`, { headers: { Accept: 'text/html' } })
            .then((response) => { if (!response.ok) throw new Error(String(response.status)); return response.text(); });
        });
        const pages = await Promise.all(requests);
        const appendedProducts = [];
        pages.forEach((markup) => {
          const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
          documentFragment.querySelectorAll('[data-kit-product]').forEach((product) => {
            appendedProducts.push(product);
            this.results.append(product);
          });
        });
        this.products = [...this.querySelectorAll('[data-kit-product]')];
        appendedProducts.forEach((product) => product.querySelector('[data-select-product]')?.addEventListener('click', () => this.selectProduct(product)));
        this.filterProducts();
        if (!this.selectInitialProduct()) this.failInitialProduct();
      } catch (error) {
        if (this.initialProductHandle) this.failInitialProduct('The color index could not be fully loaded.');
        else this.status.textContent = 'Some colors could not be loaded. Refresh and try again.';
      }
    }

    filterProducts() {
      const active = Boolean(this.state.family || this.state.finish || this.state.query);
      let totalMatches = 0;
      let shownMatches = 0;
      this.products.forEach((product) => {
        const familyMatch = !this.state.family || product.dataset.family === this.state.family;
        const finishMatch = !this.state.finish || product.dataset.finish === this.state.finish;
        const queryMatch = !this.state.query || product.dataset.title.includes(this.state.query);
        const isMatch = active && familyMatch && finishMatch && queryMatch;
        if (isMatch) totalMatches += 1;
        const visible = isMatch && shownMatches < 12;
        product.hidden = !visible;
        if (visible) shownMatches += 1;
      });
      this.results.hidden = !active;
      this.clearButton.hidden = !active;
      this.status.textContent = !active
        ? 'Choose a color family or finish to see matching basecoats.'
        : totalMatches
          ? `${totalMatches > shownMatches ? `Showing ${shownMatches} of ${totalMatches}` : totalMatches} ${totalMatches === 1 ? 'color' : 'colors'}. ${totalMatches > shownMatches ? 'Narrow the filters to see a tighter set. ' : ''}Choose one to build the kit.`
          : 'No colors match that combination. Clear a filter or try another search.';
      this.updateFilterCounts();
    }

    /*
     * Every filter button carries the number of colors it would show given the
     * other filters, and switches off when that number is zero. A pro should be
     * able to see there are no candy browns before clicking to find out.
     */
    updateFilterCounts() {
      const queryMatch = (product) => !this.state.query || product.dataset.title.includes(this.state.query);

      this.querySelectorAll('[data-filter]').forEach((button) => {
        const key = button.dataset.filter;
        const value = button.dataset.value;
        const other = key === 'family' ? 'finish' : 'family';
        const count = this.products.reduce((total, product) => {
          if (product.dataset[key] !== value) return total;
          if (this.state[other] && product.dataset[other] !== this.state[other]) return total;
          return queryMatch(product) ? total + 1 : total;
        }, 0);

        let badge = button.querySelector('[data-filter-count]');
        if (!badge) {
          badge = document.createElement('span');
          badge.dataset.filterCount = '';
          badge.className = 'paint-kit-builder__choice-count';
          button.appendChild(badge);
        }
        badge.textContent = String(count);

        // Never disable the active button — that would trap the selection.
        const selected = this.state[key] === value;
        button.disabled = count === 0 && !selected;
      });
    }

    clearFilters() {
      this.state.family = '';
      this.state.finish = '';
      this.state.query = '';
      if (this.search) this.search.value = '';
      this.querySelectorAll('[data-filter]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
      if (this.entryError) this.entryError.hidden = true;
      this.filterProducts();
      this.querySelector('[data-filter]')?.focus();
    }

    selectProduct(product, projectRecipe = null) {
      this.state.product = product;
      this.state.recipe = projectRecipe;
      if (this.entryError) this.entryError.hidden = true;
      if (projectRecipe) this.renderProjectRecipe(projectRecipe);
      else this.renderRecipe(product);
      this.seedSealerColor();
      this.toggleEpoxyColor();
      this.showStep('project');
    }

    openProjectRecipe(recipe) {
      this.state.product = null;
      this.state.recipe = recipe;
      if (this.entryError) this.entryError.hidden = true;
      this.renderProjectRecipe(recipe);
      this.seedSealerColor();
      this.toggleEpoxyColor();
      this.showStep('project');
    }

    failProjectRecipe() {
      this.showStep('look');
      this.status.textContent = 'Choose a color below to build the kit.';
      if (!this.entryError) return;
      this.entryError.textContent = 'That featured project recipe is not recognized by this builder. We did not substitute another color or product.';
      this.entryError.hidden = false;
      this.entryError.focus({ preventScroll: true });
    }

    failInitialProduct(prefix = '') {
      const colorLabel = this.initialColorName || 'That featured color';
      this.showStep('look');
      this.status.textContent = 'Choose another color below to build the kit.';
      if (!this.entryError) return;
      this.entryError.textContent = `${prefix ? `${prefix} ` : ''}${colorLabel} is not currently available in this color collection. Choose another color below.`;
      this.entryError.hidden = false;
      this.entryError.focus({ preventScroll: true });
    }

    renderRecipe(product) {
      const recipe = this.querySelector('[data-recipe-summary]');
      const content = this.querySelector('[data-recipe-content]');
      const compatibility = product?.querySelector('[data-compatibility]');
      if (!recipe || !content || !compatibility) return;

      const title = product.querySelector('.paint-kit-builder__product-copy strong')?.textContent.trim() || 'Selected basecoat';
      const heading = document.createElement('h3');
      heading.textContent = title;
      const introduction = document.createElement('p');
      introduction.textContent = 'Basecoat locked. Every product shown below works together as one Linear paint system.';
      const systemItems = [...compatibility.querySelectorAll('[data-system-role]')];
      content.replaceChildren(heading, introduction);
      if (systemItems.length) {
        const list = document.createElement('ul');
        systemItems.forEach((item) => list.append(item.cloneNode(true)));
        content.append(list);
      } else {
        const unavailable = document.createElement('p');
        unavailable.textContent = 'Basecoat selected. Your project details choose the matching primer, reducer, clearcoat, bodywork, and masking packages; vehicle size and coverage set the basecoat starting quantity.';
        content.append(unavailable);
      }
      recipe.hidden = false;
    }

    renderProjectRecipe(projectRecipe) {
      const recipe = this.querySelector('[data-recipe-summary]');
      const content = this.querySelector('[data-recipe-content]');
      if (!recipe || !content) return;
      const heading = document.createElement('h3');
      heading.textContent = projectRecipe.title;
      const introduction = document.createElement('p');
      introduction.textContent = `${projectRecipe.color} on Linear’s ${projectRecipe.vehicle}. Pick the stripe treatment, then size the system to your car.`;
      const list = document.createElement('ul');
      [['Base color', projectRecipe.color], ['Finish', projectRecipe.finish], ['Complete system', projectRecipe.system]].forEach(([label, value]) => {
        const item = document.createElement('li');
        const itemLabel = document.createElement('span');
        itemLabel.textContent = label;
        const itemValue = document.createElement('strong');
        itemValue.textContent = value;
        item.append(itemLabel, itemValue);
        list.append(item);
      });
      content.replaceChildren(heading, introduction, list);
      recipe.hidden = false;
    }

    async submitProject(event) {
      event.preventDefault();
      const form = event.currentTarget;
      const error = this.querySelector('[data-form-error]');
      if (!form.reportValidity()) {
        error.textContent = 'Finish the project choices to build your kit.';
        return;
      }
      error.textContent = '';
      this.state.project = Object.fromEntries(new FormData(form));
      // An OEMatch colour can be built here directly when the app's live price
      // resolves to a current Shopify variant. If anything about that lookup
      // fails we leave state.product null and renderSystem falls back to the
      // OEMatch handoff, which is the behaviour this replaced.
      if (!this.state.product && this.state.recipe?.oematchRecordId) {
        const submitButton = form.querySelector('button[type="submit"]');
        const originalLabel = submitButton?.textContent;
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = 'Sizing your system…';
        }
        try {
          await this.attachOematchBasecoat(this.state.recipe);
        } catch (resolveError) {
          this.state.oematch = null;
        }
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalLabel;
        }
      }
      this.renderSystem();
      this.showStep('system');
      try {
        sessionStorage.setItem(this.dataset.storageKey, JSON.stringify({
          productUrl: this.state.product?.dataset.productUrl,
          recipe: this.state.recipe?.id,
          project: this.state.project
        }));
      } catch (error) {
        // The flow still works when storage is blocked.
      }
    }

    renderSystem() {
      const product = this.state.product;
      const title = product?.querySelector('.paint-kit-builder__product-copy strong')?.textContent.trim() || this.state.recipe?.title || 'Selected color';
      const summary = this.querySelector('[data-summary]');
      const system = this.querySelector('[data-system]');
      const link = this.querySelector('[data-product-link]');
      summary.innerHTML = '';
      const heading = document.createElement('h3');
      heading.textContent = title;
      const details = document.createElement('p');
      details.textContent = `${this.state.project.basecoatPreparation} · ${this.state.project.graphics} · ${this.state.project.size} · ${this.state.project.condition} · ${this.state.project.coverage} · ${this.state.project.temperature}`;
      summary.append(heading, details);
      if (!product) {
        const kitPlan = document.createElement('div');
        kitPlan.className = 'paint-kit-builder__system-summary';
        const kitHeading = document.createElement('h3');
        kitHeading.textContent = 'Your complete paint system';
        const kitCopy = document.createElement('p');
        const vehicle = (this.state.project.size || '').toLowerCase();
        kitCopy.textContent = `Basecoat, Alpha sealer, the temperature-matched reducer, clearcoat, and prep supplies—every component chosen to spray together, sized for your ${vehicle}. Configure the color next and the complete system follows it into your cart.`;
        kitPlan.append(kitHeading, kitCopy);
        const graphicsNeedsHelp = this.needsGraphicsHelp();
        if (graphicsNeedsHelp) kitPlan.append(this.buildGraphicsNotice());
        system.replaceChildren(kitPlan);
        link.hidden = !this.state.recipe?.productUrl || graphicsNeedsHelp;
        if (this.addKitButton) this.addKitButton.hidden = true;
        if (this.state.recipe?.productUrl) {
          const productUrl = new URL(this.state.recipe.productUrl, window.location.origin);
          const isOematch = productUrl.pathname.includes('oematch-paint-all-oem-color-codes');
          if (isOematch) {
            productUrl.searchParams.set('kit', 'complete');
            productUrl.searchParams.set('recipe', this.state.recipe.id);
            productUrl.searchParams.set('color', this.state.recipe.color);
            if (this.state.recipe.formulaId) productUrl.searchParams.set('formula', this.state.recipe.formulaId);
            if (this.state.recipe.oematchRecordId) productUrl.searchParams.set('record', this.state.recipe.oematchRecordId);
            if (this.state.recipe.oematchColorId) productUrl.searchParams.set('color_id', this.state.recipe.oematchColorId);
            if (this.state.recipe.oematchRtsVariantId) productUrl.searchParams.set('rts_variant', this.state.recipe.oematchRtsVariantId);
            if (this.state.recipe.oematchNonReducedVariantId) productUrl.searchParams.set('nonreduced_variant', this.state.recipe.oematchNonReducedVariantId);
            Object.entries(this.state.project).forEach(([key, value]) => productUrl.searchParams.set(key, value));
          }
          link.href = productUrl.toString();
          link.textContent = isOematch ? 'Configure OEM paint + complete kit' : 'See this basecoat';
        }
        return;
      }
      const graphicsNeedsHelp = this.needsGraphicsHelp();
      link.hidden = false;
      if (this.addKitButton) this.addKitButton.hidden = false;
      this.renderCartManifest(product, system);
      if (graphicsNeedsHelp) system.prepend(this.buildGraphicsNotice());
      if (this.kitBlocked === true && this.addKitButton) this.addKitButton.hidden = true;
      link.href = product.dataset.productUrl;
    }

    readJson(element) {
      if (!element) return null;
      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        return null;
      }
    }

    availableVariants(product) {
      return (product?.variants || []).filter((variant) => variant.available);
    }

    /*
     * OEMatch colours are sold through a generic "OEMatch Basecoat" product
     * whose variants are price points, not colours — the colour itself only
     * exists in line-item properties. The variant that represents a given
     * colour therefore changes whenever Alpha changes pricing, so nothing here
     * may be cached in theme code. Both halves are resolved live: the current
     * price from the OEMatch app, then the variant currently sitting at that
     * price in Shopify. Any failure returns null and the caller falls back to
     * the OEMatch handoff.
     */
    async getOematchPriceMap() {
      if (this.oematchPriceMap) return this.oematchPriceMap;
      const handles = [];
      for (let index = 1; index <= 24; index += 1) {
        handles.push(index === 1 ? 'oematch-basecoat' : `oematch-basecoat-${index}`);
      }
      const products = await Promise.all(handles.map((handle) => fetch(`/products/${handle}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null)));
      const map = new Map();
      products.filter(Boolean).forEach((product) => {
        (product.variants || []).forEach((variant) => {
          if (variant.available && !map.has(variant.price)) map.set(variant.price, variant.id);
        });
      });
      this.oematchPriceMap = map.size ? map : null;
      return this.oematchPriceMap;
    }

    async attachOematchBasecoat(recipe) {
      const endpoint = 'https://apps.ecomeasy.io/tss-genie/tss-oem-colors/v2/reseller/color.php';
      const info = await fetch(`${endpoint}?id=${encodeURIComponent(recipe.oematchRecordId)}&type=basecoat`)
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
      if (!info?.prices || !info?.color) return false;
      // Never build a kit for a different colour than the featured project.
      if (recipe.formulaId && String(info.color.formula_id || '').toLowerCase() !== recipe.formulaId.toLowerCase()) return false;

      const priceMap = await this.getOematchPriceMap();
      if (!priceMap) return false;

      const sizeLabels = info.size_labels || {};
      const reducerLabels = info.reducer_labels || {};
      // Medium and slow ready-to-spray are the same price, so Shopify sells
      // them as one variant — the speed only exists as a property. Emit a
      // single ready-to-spray option carrying the label for this booth
      // temperature, otherwise two options share an id and the select lands on
      // whichever was written last.
      const readyKey = this.getTemperatureSpeed() === 'Slow'
        ? 'ready-to-spray-slow'
        : 'ready-to-spray-medium';
      const variants = [];
      ['gallon', 'quart'].forEach((sizeKey) => {
        const sizeLabel = sizeLabels[sizeKey];
        if (!sizeLabel) return;
        [readyKey, 'non-reduced'].forEach((reducerKey) => {
          const reducerLabel = reducerLabels[reducerKey];
          const price = info.prices?.[sizeKey]?.[reducerKey];
          if (!reducerLabel || typeof price !== 'number') return;
          const cents = Math.round(price * 100);
          const id = priceMap.get(cents);
          if (!id || variants.some((variant) => variant.id === id)) return;
          variants.push({
            id,
            title: `${sizeLabel} / ${reducerLabel}`,
            price: cents,
            available: true,
            options: [sizeLabel, reducerLabel]
          });
        });
      });
      // The gallon package for the chosen preparation is the line the kit is
      // built around. Without it there is nothing to fall forward to.
      const plan = window.LinearKitSizing.plan(this.state.project);
      const wantsNonReduced = this.state.project.basecoatPreparation === 'Non-reduced';
      const needle = wantsNonReduced ? 'non-reduced' : 'ready to spray';
      const gallon = variants.find((variant) => variant.title.toLowerCase().includes('gallon')
        && variant.title.toLowerCase().includes(needle));
      if (!gallon) return false;
      if (plan.quarts > 0) {
        const quart = variants.find((variant) => variant.title.toLowerCase().includes('quart')
          && variant.title.toLowerCase().includes(needle));
        if (!quart) return false;
      }

      const colorTitle = [info.color.make, info.color.name, info.color.oem_code]
        .filter(Boolean).join(' ');
      const element = document.createElement('article');
      element.dataset.kitProduct = '';
      element.dataset.productUrl = recipe.productUrl || '#';
      element.dataset.productHandle = 'oematch-basecoat';
      const json = document.createElement('script');
      json.type = 'application/json';
      json.dataset.basecoatProductJson = '';
      json.textContent = JSON.stringify({
        handle: 'oematch-basecoat',
        title: colorTitle,
        url: recipe.productUrl || '#',
        variants
      });
      const copy = document.createElement('span');
      copy.className = 'paint-kit-builder__product-copy';
      const strong = document.createElement('strong');
      strong.textContent = colorTitle;
      copy.append(strong);
      element.append(json, copy);

      this.state.product = element;
      // Written onto every OEMatch basecoat line so the order carries the same
      // colour identity the app's own cart request would have carried.
      // The app's API returns these upper-cased; its own widget title-cases
      // them before writing the cart line. Match that so an order built here
      // reads identically to one built through the widget.
      const titleCase = (value) => String(value || '')
        .toLowerCase()
        .replace(/(^|\s)([a-z])/g, (match, lead, letter) => lead + letter.toUpperCase());
      this.state.oematch = {
        Make: titleCase(info.color.make),
        'Color name': titleCase(info.color.name),
        'Color code': info.color.oem_code,
        _color_id: String(info.color.color_id),
        _formula_id: String(info.color.formula_id),
        _tss_source: 'embed/v1'
      };
      return true;
    }

    findVariant(product, requiredText = []) {
      const variants = this.availableVariants(product);
      const tokens = requiredText.map((value) => String(value).toLowerCase());
      return variants.find((variant) => tokens.every((token) => variant.title.toLowerCase().includes(token))) || null;
    }

    // Reducer follows Alpha's spec; clearcoat runs a step slower on purpose.
    // Both tables live in assets/linear-kit-sizing.js.
    getTemperatureSpeed(role = 'reducer') {
      return window.LinearKitSizing.speedFor(role, this.state.project.temperature);
    }

    // The customer's choice wins. Everything else is the suggestion we seed the
    // radio with when they reach the project step.
    getSealerColor(product) {
      if (this.state.project.sealerColor) return this.state.project.sealerColor;
      return this.suggestSealerColor(product);
    }

    suggestSealerColor(product) {
      if (this.state.recipe?.sealerColor) return this.state.recipe.sealerColor;
      const label = `${product?.title || ''} ${this.state.recipe?.color || ''} ${this.state.product?.dataset.title || ''}`.toLowerCase();
      if (/black|void|luxor|midnight|raven|onyx/.test(label)) return 'Black';
      if (/white|cream|ivory|beige|champagne|frost|silver|yellow|orange|gold/.test(label)) return 'White';
      return 'Gray';
    }

    // Seed the sealer-colour radio from the basecoat before the customer sees it.
    seedSealerColor() {
      const suggested = this.suggestSealerColor(null);
      const radio = this.querySelector(`input[name="sealerColor"][value="${suggested}"]`);
      if (radio) radio.checked = true;
    }

    /*
     * Epoxy only ships when the job needs bodywork, so only ask for its colour
     * then. It is a separate question from the sealer: the sealer comes in
     * black/gray/white and the epoxy in blue/gray/black, so one answer cannot
     * serve both.
     */
    toggleEpoxyColor() {
      const field = this.querySelector('[data-epoxy-color-field]');
      if (!field) return;
      const condition = this.querySelector('input[name="condition"]:checked')?.value || '';
      field.hidden = !window.LinearKitSizing?.needsBodywork(condition);
    }

    // Quantities come from assets/linear-kit-sizing.js so the builder and the
    // OEMatch bridge stay in step. Edit the tables there, not here.
    getBasecoatPlan() {
      return window.LinearKitSizing.plan(this.state.project);
    }

    getRecommendedVariant(role, product, basecoat) {
      const speed = this.getTemperatureSpeed();
      const sealerColor = this.getSealerColor(basecoat);
      const wantsNonReduced = this.state.project.basecoatPreparation === 'Non-reduced';
      if (role === 'basecoat' && wantsNonReduced) return this.findVariant(product, ['Gallon (128oz)', 'Non-Reduced']);
      if (role === 'basecoat') return this.findVariant(product, ['Gallon (128oz)', 'Ready To Spray', `${speed} Reducer`]);
      if (role === 'basecoat-topup' && wantsNonReduced) return this.findVariant(product, ['Quart (32oz)', 'Non-Reduced']);
      if (role === 'basecoat-topup') return this.findVariant(product, ['Quart (32oz)', 'Ready To Spray', `${speed} Reducer`]);
      if (role.startsWith('stripe')) return this.findVariant(product, ['Quart (32oz)', 'Ready To Spray', `${speed} Reducer`]);
      if (role === 'sealer') {
        const plan = this.getBasecoatPlan();
        return this.findVariant(product, [plan.sealerLarge > 0 ? '4.5 Liter' : '1.25 Liter', sealerColor]);
      }
      if (role === 'sealer-topup') return this.findVariant(product, ['1.25 Liter', sealerColor]);
      // Reducer is bought as 5L plus 1L top-ups, so the kit lands just over what
      // the mix ratios call for instead of a whole package over.
      if (role === 'reducer') {
        const plan = this.getBasecoatPlan();
        return this.findVariant(product, [plan.reducerLarge > 0 ? '5L' : '1L', speed]);
      }
      if (role === 'reducer-topup') return this.findVariant(product, ['1L', speed]);
      if (role === 'clearcoat') {
        const plan = this.getBasecoatPlan();
        return this.findVariant(product, [plan.clearcoatLarge > 0 ? '7.5L' : '1.5L', this.getTemperatureSpeed('clearcoat')]);
      }
      if (role === 'clearcoat-topup') return this.findVariant(product, ['1.5L', this.getTemperatureSpeed('clearcoat')]);
      // Epoxy carries its own colour. It used to reuse sealerColor, which meant a
      // White sealer found no matching epoxy and silently fell back to Gray, and
      // Linear blue was unreachable from any answer.
      if (role === 'primer') {
        const epoxyColor = this.state.project.epoxyColor || 'Blue';
        return this.findVariant(product, [epoxyColor, 'Gallon Kit']) || this.findVariant(product, ['Gray', 'Gallon Kit']);
      }
      if (role === 'masking') return this.findVariant(product, ['12 (Sleeve)']);
      if (role === 'masking-film') return this.findVariant(product, ['180cm']);
      return this.findVariant(product);
    }

    getManifestProducts(basecoat) {
      const needsBodywork = window.LinearKitSizing.needsBodywork(this.state.project.condition);
      const fullColorChange = this.state.project.coverage === 'Full color change';
      const reducerLabel = this.state.project.basecoatPreparation === 'Non-reduced'
        ? 'Reducer for basecoat + sealer'
        : 'Reducer for Alpha sealer';
      const plan = this.getBasecoatPlan();
      const wantsStripes = this.needsGraphicsHelp();
      const bodyworkGroup = needsBodywork ? 'Bodywork' : 'Bodywork — when needed';
      return [
        { group: 'Paint system', role: 'basecoat', label: 'Basecoat', product: basecoat, required: true, included: true },
        ...(plan.quarts > 0
          ? [{ group: 'Paint system', role: 'basecoat-topup', label: 'Basecoat top-up', product: basecoat, required: false, included: true }]
          : []),
        ...(wantsStripes
          ? [
              { group: 'Stripes & graphics', role: 'stripe-black', label: 'Stripe color — black', product: this.systemCatalog.stripeBlack, required: false, included: false },
              { group: 'Stripes & graphics', role: 'stripe-white', label: 'Stripe color — white', product: this.systemCatalog.stripeWhite, required: false, included: false },
              { group: 'Stripes & graphics', role: 'stripe-cream', label: 'Stripe color — cream', product: this.systemCatalog.stripeCream, required: false, included: false },
            ]
          : []),
        { group: 'Paint system', role: 'sealer', label: 'Alpha sealer', product: this.systemCatalog.sealer, required: true, included: true },
        ...(plan.sealerLarge > 0 && plan.sealerSmall > 0
          ? [{ group: 'Paint system', role: 'sealer-topup', label: 'Sealer top-up', product: this.systemCatalog.sealer, required: false, included: true }]
          : []),
        { group: 'Paint system', role: 'reducer', label: reducerLabel, product: this.systemCatalog.reducer, required: true, included: true },
        ...(plan.reducerLarge > 0 && plan.reducerSmall > 0
          ? [{ group: 'Paint system', role: 'reducer-topup', label: 'Reducer top-up', product: this.systemCatalog.reducer, required: false, included: true }]
          : []),
        { group: 'Paint system', role: 'clearcoat', label: 'Clearcoat kit', product: this.systemCatalog.clearcoat, required: true, included: true },
        ...(plan.clearcoatLarge > 0 && plan.clearcoatSmall > 0
          ? [{ group: 'Paint system', role: 'clearcoat-topup', label: 'Clearcoat top-up', product: this.systemCatalog.clearcoat, required: false, included: true }]
          : []),
        { group: bodyworkGroup, role: 'primer', label: 'Linear epoxy primer', product: this.systemCatalog.primer, required: needsBodywork, included: needsBodywork },
        { group: bodyworkGroup, role: 'bodywork', label: 'Body filler', product: this.systemCatalog.filler, required: false, included: needsBodywork },
        { group: 'Prep, masking & cleanup', role: 'masking', label: 'Masking tape', product: this.systemCatalog.masking, required: false, included: true },
        { group: 'Prep, masking & cleanup', role: 'masking-film', label: 'Masking film', product: this.systemCatalog.maskingFilm, required: false, included: true },
        { group: 'Prep, masking & cleanup', role: 'cleaning-cloths', label: 'Clean microfiber cloths', product: this.systemCatalog.microfiber, required: false, included: true },
        { group: 'Prep, masking & cleanup', role: 'foam-masking', label: 'Foam masking tape', product: this.systemCatalog.foamTape, required: false, included: fullColorChange },
        { group: 'Prep, masking & cleanup', role: 'trim-masking', label: 'Trim masking tape', product: this.systemCatalog.trimTape, required: false, included: fullColorChange }
      ].filter((entry) => entry.required || this.availableVariants(entry.product).length);
    }

    getManifestVariants(entry, recommended) {
      const variants = this.availableVariants(entry.product);
      if (entry.required) return recommended ? [recommended] : [];
      if (entry.role !== 'basecoat') return variants;
      const requiredLabel = this.state.project.basecoatPreparation === 'Non-reduced' ? 'non-reduced' : 'ready to spray';
      return variants.filter((variant) => variant.title.toLowerCase().includes(requiredLabel));
    }

    needsGraphicsHelp() {
      return this.state.project.graphics !== 'No additional graphics';
    }

    buildGraphicsNotice() {
      const notice = document.createElement('div');
      notice.className = 'paint-kit-builder__configuration-error';
      notice.setAttribute('role', 'status');
      const documentedColors = this.state.recipe?.unresolvedGraphics?.join(', ');
      const explanation = documentedColors
        ? `On Linear's build the graphics were ${documentedColors}.`
        : 'Stripes and graphics spray as additional basecoat colors over the same system.';
      notice.innerHTML = `<strong>Stripes are extra basecoat colors.</strong><span>${explanation} This kit covers your base color, sealer, reducer, clear, and prep. Add a quart of each stripe color from the color list to spray them over the same system.</span>`;
      return notice;
    }

    getStartingQuantity(role) {
      const plan = this.getBasecoatPlan();
      if (role === 'basecoat') return plan.gallons;
      if (role === 'basecoat-topup') return plan.quarts;
      if (role === 'reducer') return plan.reducerLarge || plan.reducerSmall;
      if (role === 'reducer-topup') return plan.reducerSmall;
      if (role === 'sealer') return plan.sealerLarge || plan.sealerSmall;
      if (role === 'sealer-topup') return plan.sealerSmall;
      if (role === 'clearcoat') return plan.clearcoatLarge || plan.clearcoatSmall;
      if (role === 'clearcoat-topup') return plan.clearcoatSmall;
      if (role === 'primer') return plan.epoxyPrimerGallons || 1;
      return 1;
    }

    renderCartManifest(productElement, system) {
      const basecoat = this.readJson(productElement.querySelector('[data-basecoat-product-json]'));
      const entries = this.getManifestProducts(basecoat);
      const manifest = document.createElement('div');
      manifest.className = 'paint-kit-builder__manifest';

      this.kitBlocked = false;
      const unresolvedRequired = entries.filter((entry) => entry.required && !this.getRecommendedVariant(entry.role, entry.product, basecoat));
      if (unresolvedRequired.length) {
        this.kitBlocked = true;
        const unavailable = document.createElement('div');
        unavailable.className = 'paint-kit-builder__configuration-error';
        unavailable.setAttribute('role', 'alert');
        unavailable.innerHTML = `<strong>This complete kit cannot be configured right now.</strong><span>These components are out of stock in the package this kit needs: ${unresolvedRequired.map((entry) => entry.label).join(', ')}. Choose another color, or add the remaining components from the catalog.</span>`;
        system.replaceChildren(unavailable);
        if (this.addKitButton) this.addKitButton.hidden = true;
        return;
      }

      const intro = document.createElement('p');
      intro.className = 'paint-kit-builder__manifest-intro';
      intro.textContent = 'One complete Linear system, sized from your vehicle, coverage, and basecoat preparation to finish with paint left over rather than run short mid-panel. Adjust any quantity before the kit goes to cart.';
      manifest.append(intro);

      const prepReminder = document.createElement('div');
      prepReminder.className = 'paint-kit-builder__prep-reminder';
      prepReminder.innerHTML = '<strong>Surface-prep checkpoint</strong><span>Have wax and grease remover plus tack cloths on hand before spraying. Addable masking and clean-cloth supplies are included below.</span>';
      manifest.append(prepReminder);

      const groups = new Map();
      entries.forEach((entry) => {
        if (!groups.has(entry.group)) {
          const group = document.createElement('section');
          group.className = 'paint-kit-builder__manifest-group';
          const groupHeading = document.createElement('h3');
          groupHeading.textContent = entry.group;
          group.append(groupHeading);
          groups.set(entry.group, group);
          manifest.append(group);
        }
        const recommended = this.getRecommendedVariant(entry.role, entry.product, basecoat);
        const line = document.createElement('div');
        line.className = 'paint-kit-builder__cart-line';
        line.dataset.kitRole = entry.role;

        const productLabel = document.createElement('label');
        productLabel.className = 'paint-kit-builder__cart-product';
        const include = document.createElement('input');
        include.type = 'checkbox';
        include.checked = entry.included;
        include.disabled = entry.required;
        include.dataset.includeKitLine = '';
        const copy = document.createElement('span');
        const role = document.createElement('span');
        role.textContent = entry.label;
        const title = document.createElement('strong');
        title.textContent = entry.product.title;
        copy.append(role, title);
        productLabel.append(include, copy);

        const variantLabel = document.createElement('label');
        variantLabel.className = 'paint-kit-builder__cart-control';
        variantLabel.append('Package');
        const select = document.createElement('select');
        select.dataset.variantSelect = '';
        this.getManifestVariants(entry, recommended).forEach((variant) => {
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.dataset.price = String(variant.price);
          option.textContent = `${variant.title} — ${this.formatMoney(variant.price)}`;
          option.selected = variant.id === recommended?.id;
          select.append(option);
        });
        variantLabel.append(select);

        const quantityLabel = document.createElement('label');
        quantityLabel.className = 'paint-kit-builder__cart-control';
        quantityLabel.append('Qty');
        const quantity = document.createElement('input');
        quantity.type = 'number';
        quantity.min = '1';
        quantity.step = '1';
        quantity.value = String(this.getStartingQuantity(entry.role));
        quantity.inputMode = 'numeric';
        quantity.dataset.kitQuantity = '';
        quantityLabel.append(quantity);

        line.append(productLabel, variantLabel, quantityLabel);
        line.addEventListener('change', () => this.updateKitTotal());
        groups.get(entry.group).append(line);
      });

      const total = document.createElement('div');
      total.className = 'paint-kit-builder__kit-total';
      total.innerHTML = '<span>Kit total</span><strong data-kit-total></strong>';
      manifest.append(total);
      system.replaceChildren(manifest);
      this.updateKitTotal();
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.dataset.currency || 'USD' }).format(Number(cents || 0) / 100);
    }

    updateKitTotal() {
      let cents = 0;
      this.querySelectorAll('.paint-kit-builder__cart-line').forEach((line) => {
        const include = line.querySelector('[data-include-kit-line]');
        if (!include?.checked) return;
        const selected = line.querySelector('[data-variant-select]')?.selectedOptions?.[0];
        const quantity = Math.max(1, Number(line.querySelector('[data-kit-quantity]')?.value || 1));
        cents += Number(selected?.dataset.price || 0) * quantity;
      });
      const total = this.querySelector('[data-kit-total]');
      if (total) total.textContent = this.formatMoney(cents);
    }

    async addCompleteKit() {
      if (!this.state.product || !this.addKitButton) return;
      const lines = [...this.querySelectorAll('.paint-kit-builder__cart-line')];
      const kitId = `linear-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const properties = {
        '_Kit ID': kitId,
        'Paint color': this.state.recipe?.color || this.state.product.querySelector('.paint-kit-builder__product-copy strong')?.textContent.trim() || 'Selected color',
        'Vehicle size': this.state.project.size,
        'Coverage': this.state.project.coverage,
        'Condition': this.state.project.condition,
        'Booth temperature': this.state.project.temperature,
        'Graphics choice': this.state.project.graphics,
        'Basecoat preparation': this.state.project.basecoatPreparation
      };
      const items = lines.filter((line) => line.querySelector('[data-include-kit-line]')?.checked).map((line) => {
        const select = line.querySelector('[data-variant-select]');
        const id = Number(select?.value);
        const quantity = Number(line.querySelector('[data-kit-quantity]')?.value);
        const lineProperties = { ...properties, '_Kit role': line.dataset.kitRole };
        // An OEMatch basecoat variant carries no colour of its own, so the
        // colour identity and the package it represents travel as properties.
        if (this.state.oematch && /^basecoat/.test(line.dataset.kitRole)) {
          const option = select?.selectedOptions?.[0];
          const [size, reducer] = (option?.textContent || '').split(' — ')[0].split(' / ');
          Object.assign(lineProperties, this.state.oematch, {
            Size: size || '',
            Reducer: reducer || ''
          });
        }
        return { id, quantity, properties: lineProperties };
      });

      const requiredRoles = ['basecoat', 'sealer', 'reducer', 'clearcoat'];
      const selectedRoles = new Set(lines
        .filter((line) => line.querySelector('[data-include-kit-line]')?.checked)
        .map((line) => line.dataset.kitRole));
      const basecoat = this.readJson(this.state.product.querySelector('[data-basecoat-product-json]'));
      const manifestByRole = new Map(this.getManifestProducts(basecoat).map((entry) => [entry.role, entry]));
      const invalid = items.some((item) => !Number.isInteger(item.id) || !Number.isInteger(item.quantity) || item.quantity < 1);
      const missingRequiredRole = requiredRoles.some((role) => !selectedRoles.has(role));
      const invalidRequiredPackage = requiredRoles.some((role) => {
        const line = lines.find((candidate) => candidate.dataset.kitRole === role);
        const entry = manifestByRole.get(role);
        const recommended = entry ? this.getRecommendedVariant(role, entry.product, basecoat) : null;
        return !line || !recommended || Number(line.querySelector('[data-variant-select]')?.value) !== Number(recommended.id);
      });
      if (invalid || missingRequiredRole || invalidRequiredPackage) {
        this.cartStatus.textContent = 'Choose a valid package and quantity for every required kit line.';
        return;
      }

      this.addKitButton.disabled = true;
      this.addKitButton.textContent = 'Adding the complete kit…';
      this.cartStatus.textContent = '';
      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'The kit could not be added.');
        }
        await this.settleCartApps();
        window.location.assign(window.routes?.cart_url || '/cart');
      } catch (error) {
        this.cartStatus.textContent = error.message || 'The kit could not be added. Try again.';
        this.addKitButton.disabled = false;
        this.addKitButton.textContent = 'Add the complete kit to cart';
      }
    }

    /*
     * Apps that price off cart contents — Order Protection among them — react to
     * the theme's cart-update event. The kit used to add every line in one
     * /cart/add.js call, announce nothing, and navigate to /cart immediately, so
     * protection was never added and the customer landed on a cart the widget
     * had not seen. Announce the change, then give those apps a bounded moment
     * to add their line before the page goes away.
     */
    async settleCartApps() {
      const cartUrl = window.routes?.cart_url || '/cart';
      const readCart = () => fetch(`${cartUrl}.js`, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);

      const before = await readCart();
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'paint-kit-builder', cartData: before });
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: before } }));

      const baseline = Number(before?.item_count || 0);
      for (let attempt = 0; attempt < 6; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 250));
        const next = await readCart();
        if (Number(next?.item_count || 0) > baseline) return;
      }
    }

    showStep(name) {
      this.steps.forEach((step) => { step.hidden = step.dataset.step !== name; });
      this.querySelectorAll('[data-progress]').forEach((item) => {
        if (item.dataset.progress === name) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
      const current = this.querySelector(`[data-step="${name}"]`);
      current?.querySelector('h2, h1')?.focus({ preventScroll: true });
      current?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }
  });
}
